import React from 'react'
import { Box } from '@mui/material'

const AnimatedEditorShowcase: React.FC = () => {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '750px',
        aspectRatio: '4/3',
        position: 'relative',
        filter: 'drop-shadow(0 30px 60px rgba(0, 0, 0, 0.25))',
      }}
    >
      <svg
        width='100%'
        height='100%'
        viewBox='0 0 750 563'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <defs>
          {/* Premium Gradients */}
          <linearGradient id='editorBg' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor='#0f172a' />
            <stop offset='100%' stopColor='#1e293b' />
          </linearGradient>

          <linearGradient id='sidebarBg' x1='0%' y1='0%' x2='0%' y2='100%'>
            <stop offset='0%' stopColor='#ffffff' />
            <stop offset='100%' stopColor='#f8fafc' />
          </linearGradient>

          <linearGradient id='canvasBg' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor='rgba(15, 23, 42, 0.2)' />
            <stop offset='100%' stopColor='rgba(30, 41, 59, 0.2)' />
          </linearGradient>

          <linearGradient id='glowGradient' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor='rgba(99, 102, 241, 0.08)' />
            <stop offset='100%' stopColor='rgba(236, 72, 153, 0.08)' />
          </linearGradient>

          {/* Pattern Gradients */}
          <linearGradient id='pattern1' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor='#6366f1' />
            <stop offset='100%' stopColor='#8b5cf6' />
          </linearGradient>

          <linearGradient id='pattern2' x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor='#ec4899' />
            <stop offset='100%' stopColor='#f43f5e' />
          </linearGradient>

          <linearGradient id='pattern3' x1='0%' y1='0%' x2='100%' y2='100%'>
            <stop offset='0%' stopColor='#10b981' />
            <stop offset='100%' stopColor='#059669' />
          </linearGradient>

          <linearGradient id='pattern4' x1='0%' y1='0%' x2='0%' y2='100%'>
            <stop offset='0%' stopColor='#f59e0b' />
            <stop offset='100%' stopColor='#ef4444' />
          </linearGradient>

          {/* Print Patterns */}
          <pattern id='dots' patternUnits='userSpaceOnUse' width='20' height='20'>
            <circle cx='10' cy='10' r='3' fill='#6366f1' />
          </pattern>

          <pattern
            id='stripes'
            patternUnits='userSpaceOnUse'
            width='20'
            height='20'
            patternTransform='rotate(45)'
          >
            <rect width='10' height='20' fill='#ec4899' />
          </pattern>

          <pattern id='chevron' patternUnits='userSpaceOnUse' width='30' height='30'>
            <path
              d='M 0 15 L 15 0 L 30 15 L 15 30 Z'
              fill='none'
              stroke='#8b5cf6'
              strokeWidth='2'
            />
          </pattern>

          <pattern id='geometric' patternUnits='userSpaceOnUse' width='40' height='40'>
            <circle cx='20' cy='20' r='12' fill='none' stroke='#10b981' strokeWidth='2' />
            <rect x='8' y='8' width='24' height='24' fill='none' stroke='#10b981' strokeWidth='2' />
          </pattern>

          <pattern id='stars' patternUnits='userSpaceOnUse' width='30' height='30'>
            <path
              d='M 15 5 L 17 12 L 25 12 L 19 17 L 21 25 L 15 20 L 9 25 L 11 17 L 5 12 L 13 12 Z'
              fill='#f59e0b'
            />
          </pattern>

          {/* Filters */}
          <filter id='shadow' x='-50%' y='-50%' width='200%' height='200%'>
            <feGaussianBlur in='SourceAlpha' stdDeviation='6' />
            <feOffset dx='0' dy='4' result='offsetblur' />
            <feComponentTransfer>
              <feFuncA type='linear' slope='0.2' />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>

          <filter id='glow'>
            <feGaussianBlur stdDeviation='5' result='coloredBlur' />
            <feMerge>
              <feMergeNode in='coloredBlur' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>

          <filter id='softGlow'>
            <feGaussianBlur stdDeviation='3' result='coloredBlur' />
            <feMerge>
              <feMergeNode in='coloredBlur' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
        </defs>

        {/* Main Container */}
        <rect
          width='750'
          height='563'
          rx='20'
          fill='url(#editorBg)'
          stroke='rgba(99, 102, 241, 0.3)'
          strokeWidth='2'
        />
        <rect
          x='2'
          y='2'
          width='746'
          height='559'
          rx='18'
          fill='none'
          stroke='rgba(255, 255, 255, 0.05)'
          strokeWidth='1'
        />

        {/* Top Toolbar */}
        <rect width='750' height='60' rx='20' fill='rgba(30, 41, 59, 0.9)' />
        <line x1='0' y1='60' x2='750' y2='60' stroke='rgba(255, 255, 255, 0.1)' strokeWidth='1' />

        {/* Toolbar Icons */}
        <g transform='translate(25, 17)'>
          <rect width='28' height='28' rx='7' fill='rgba(99, 102, 241, 0.2)' />
          <rect
            x='4'
            y='4'
            width='20'
            height='20'
            rx='5'
            fill='#6366f1'
            // filter='url(#softGlow)'
          >
            <animate attributeName='opacity' values='1;0.7;1' dur='2s' repeatCount='indefinite' />
          </rect>
        </g>
        <g transform='translate(70, 17)'>
          <circle
            cx='14'
            cy='14'
            r='12'
            stroke='#9ca3af'
            strokeWidth='2.5'
            fill='none'
            opacity='0.8'
          />
        </g>
        <g transform='translate(115, 17)'>
          <rect
            x='3'
            y='3'
            width='22'
            height='22'
            rx='4'
            stroke='#9ca3af'
            strokeWidth='2.5'
            fill='none'
            opacity='0.8'
          />
        </g>

        {/* Live indicator - LEFT of Export */}
        <g transform='translate(580, 15)'>
          <circle cx='0' cy='15' r='7' fill='#10b981'>
            <animate attributeName='opacity' values='1;0.5;1' dur='2s' repeatCount='indefinite' />
          </circle>
          <text x='15' y='20' fill='#94a3b8' fontSize='13' fontWeight='600'>
            Live
          </text>
        </g>

        {/* Right Toolbar Button */}
        <g transform='translate(640, 15)'>
          <rect
            width='90'
            height='30'
            rx='8'
            fill='rgba(99, 102, 241, 0.15)'
            stroke='#6366f1'
            strokeWidth='1'
          >
            <animate
              attributeName='fill'
              values='rgba(99, 102, 241, 0.15);rgba(99, 102, 241, 0.25);rgba(99, 102, 241, 0.15)'
              dur='3s'
              repeatCount='indefinite'
            />
          </rect>
          <text x='45' y='20' textAnchor='middle' fill='#a5b4fc' fontSize='13' fontWeight='700'>
            Export
          </text>
        </g>

        {/* Left Sidebar */}
        <rect x='0' y='60' width='110' height='503' fill='url(#sidebarBg)' />
        <line x1='110' y1='60' x2='110' y2='563' stroke='rgba(99, 102, 241, 0.2)' strokeWidth='1' />

        {/* ===== NAV ICON 1: FRAMES (0-4s) - TIGHTER SPACING ===== */}
        <g>
          {/* Background card - reduced height */}
          <rect
            x='20'
            y='80'
            width='70'
            height='70'
            rx='12'
            fill='white'
            // filter='url(#shadow)'
            opacity='0.4'
          >
            <animate
              attributeName='opacity'
              values='0.4;1;1;1;0.4;0.4;0.4;0.4;0.4'
              keyTimes='0;0.025;0.15;0.19;0.21;0.4;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect
            x='20'
            y='80'
            width='70'
            height='70'
            rx='12'
            fill='none'
            stroke='#6366f1'
            strokeWidth='0'
            opacity='1'
          >
            <animate
              attributeName='stroke-width'
              values='0;2.5;2.5;2.5;0;0;0;0;0'
              keyTimes='0;0.025;0.15;0.19;0.21;0.4;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          {/* Frame icon centered - SLIGHTLY BIGGER */}
          <rect
            x='41'
            y='93'
            width='28'
            height='24'
            rx='3'
            fill='none'
            stroke='#9ca3af'
            strokeWidth='2.5'
          >
            <animate
              attributeName='stroke'
              values='#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.025;0.15;0.19;0.21;0.4;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='45' y='97' width='20' height='16' rx='2' fill='#9ca3af' opacity='0.3'>
            <animate
              attributeName='fill'
              values='#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.025;0.15;0.19;0.21;0.4;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          {/* Text closer to icon */}
          <text x='55' y='138' textAnchor='middle' fill='#64748b' fontSize='10' fontWeight='600'>
            <animate
              attributeName='fill'
              values='#64748b;#6366f1;#6366f1;#6366f1;#64748b;#64748b;#64748b;#64748b;#64748b'
              keyTimes='0;0.025;0.15;0.19;0.21;0.4;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='font-weight'
              values='600;700;700;700;600;600;600;600;600'
              keyTimes='0;0.025;0.15;0.19;0.21;0.4;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
            Frames
          </text>
        </g>

        {/* ===== NAV ICON 2: PATTERNS (4-8s) ===== */}
        <g>
          <rect
            x='20'
            y='165'
            width='70'
            height='70'
            rx='12'
            fill='white'
            // filter='url(#shadow)'
            opacity='0.4'
          >
            <animate
              attributeName='opacity'
              values='0.4;0.4;1;1;1;0.4;0.4;0.4;0.4'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect
            x='20'
            y='165'
            width='70'
            height='70'
            rx='12'
            fill='none'
            stroke='#6366f1'
            strokeWidth='0'
            opacity='1'
          >
            <animate
              attributeName='stroke-width'
              values='0;0;2.5;2.5;2.5;0;0;0;0'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          {/* 3x3 grid icon */}
          <rect x='41' y='178' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#8b5cf6;#8b5cf6;#8b5cf6;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='51' y='178' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#ec4899;#ec4899;#ec4899;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='61' y='178' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#10b981;#10b981;#10b981;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='41' y='188' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#f59e0b;#f59e0b;#f59e0b;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='51' y='188' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='61' y='188' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#ef4444;#ef4444;#ef4444;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='41' y='198' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#14b8a6;#14b8a6;#14b8a6;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='51' y='198' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#8b5cf6;#8b5cf6;#8b5cf6;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect x='61' y='198' width='8' height='8' rx='1.5' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#f59e0b;#f59e0b;#f59e0b;#9ca3af;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <text x='55' y='223' textAnchor='middle' fill='#64748b' fontSize='10' fontWeight='600'>
            <animate
              attributeName='fill'
              values='#64748b;#64748b;#6366f1;#6366f1;#6366f1;#64748b;#64748b;#64748b;#64748b'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='font-weight'
              values='600;600;700;700;700;600;600;600;600'
              keyTimes='0;0.19;0.21;0.35;0.39;0.41;0.6;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
            Patterns
          </text>
        </g>

        {/* ===== NAV ICON 3: COLORS (8-12s) ===== */}
        <g>
          <rect
            x='20'
            y='250'
            width='70'
            height='70'
            rx='12'
            fill='white'
            //filter='url(#shadow)'
            opacity='0.4'
          >
            <animate
              attributeName='opacity'
              values='0.4;0.4;0.4;1;1;1;0.4;0.4;0.4'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect
            x='20'
            y='250'
            width='70'
            height='70'
            rx='12'
            fill='none'
            stroke='#6366f1'
            strokeWidth='0'
            opacity='1'
          >
            <animate
              attributeName='stroke-width'
              values='0;0;0;2.5;2.5;2.5;0;0;0'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          {/* Colors icon - OVERLAPPING CIRCLES (Clean & Modern) */}
          {/* Background circle - BIGGER */}
          <circle cx='55' cy='278' r='16' fill='none' stroke='#9ca3af' strokeWidth='2.5'>
            <animate
              attributeName='stroke'
              values='#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>

          {/* Top circle - Indigo (smaller) */}
          <circle cx='55' cy='272' r='5' fill='#9ca3af' opacity='0.9'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>

          {/* Left circle - Green (smaller) */}
          <circle cx='48' cy='282' r='5' fill='#9ca3af' opacity='0.9'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#10b981;#10b981;#10b981;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>

          {/* Right circle - Pink (smaller) */}
          <circle cx='62' cy='282' r='5' fill='#9ca3af' opacity='0.9'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#ec4899;#ec4899;#ec4899;#9ca3af;#9ca3af;#9ca3af'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>
          <text x='55' y='308' textAnchor='middle' fill='#64748b' fontSize='10' fontWeight='600'>
            <animate
              attributeName='fill'
              values='#64748b;#64748b;#64748b;#6366f1;#6366f1;#6366f1;#64748b;#64748b;#64748b'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='font-weight'
              values='600;600;600;700;700;700;600;600;600'
              keyTimes='0;0.39;0.41;0.43;0.55;0.59;0.61;0.8;1'
              dur='20s'
              repeatCount='indefinite'
            />
            Colors
          </text>
        </g>

        {/* ===== NAV ICON 4: MESH (12-16s) ===== */}
        <g>
          <rect
            x='20'
            y='335'
            width='70'
            height='70'
            rx='12'
            fill='white'
            // filter='url(#shadow)'
            opacity='0.4'
          >
            <animate
              attributeName='opacity'
              values='0.4;0.4;0.4;0.4;1;1;1;0.4;0.4'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect
            x='20'
            y='335'
            width='70'
            height='70'
            rx='12'
            fill='none'
            stroke='#6366f1'
            strokeWidth='0'
            opacity='1'
          >
            <animate
              attributeName='stroke-width'
              values='0;0;0;0;2.5;2.5;2.5;0;0'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          {/* Mesh icon */}
          <path
            d='M 43 350 L 67 350 L 67 374 L 43 374 Z M 43 350 L 67 374 M 67 350 L 43 374 M 55 350 L 55 374 M 43 362 L 67 362'
            stroke='#9ca3af'
            strokeWidth='2'
            fill='none'
          >
            <animate
              attributeName='stroke'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#8b5cf6;#8b5cf6;#8b5cf6;#9ca3af;#9ca3af'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </path>
          <circle cx='43' cy='350' r='3' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>
          <circle cx='67' cy='350' r='3' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>
          <circle cx='67' cy='374' r='3' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>
          <circle cx='43' cy='374' r='3' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>
          <circle cx='55' cy='362' r='3' fill='#9ca3af'>
            <animate
              attributeName='fill'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#6366f1;#6366f1;#6366f1;#9ca3af;#9ca3af'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </circle>
          <text x='55' y='393' textAnchor='middle' fill='#64748b' fontSize='10' fontWeight='600'>
            <animate
              attributeName='fill'
              values='#64748b;#64748b;#64748b;#64748b;#6366f1;#6366f1;#6366f1;#64748b;#64748b'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='font-weight'
              values='600;600;600;600;700;700;700;600;600'
              keyTimes='0;0.59;0.61;0.63;0.65;0.75;0.79;0.81;1'
              dur='20s'
              repeatCount='indefinite'
            />
            Mesh
          </text>
        </g>

        {/* ===== NAV ICON 5: EXPORT (16-20s) ===== */}
        <g>
          <rect
            x='20'
            y='420'
            width='70'
            height='70'
            rx='12'
            fill='white'
            // filter='url(#shadow)'
            opacity='0.4'
          >
            <animate
              attributeName='opacity'
              values='0.4;0.4;0.4;0.4;0.4;1;1;1;0'
              keyTimes='0;0.79;0.81;0.83;0.85;0.87;0.95;0.99;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          <rect
            x='20'
            y='420'
            width='70'
            height='70'
            rx='12'
            fill='none'
            stroke='#10b981'
            strokeWidth='0'
            opacity='1'
          >
            <animate
              attributeName='stroke-width'
              values='0;0;0;0;0;2.5;2.5;2.5;0'
              keyTimes='0;0.79;0.81;0.83;0.85;0.87;0.95;0.99;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </rect>
          {/* Export icon */}
          <path
            d='M 43 455 L 43 463 L 67 463 L 67 455 M 55 437 L 55 453 M 51 441 L 55 437 L 59 441'
            stroke='#9ca3af'
            strokeWidth='2'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <animate
              attributeName='stroke'
              values='#9ca3af;#9ca3af;#9ca3af;#9ca3af;#9ca3af;#10b981;#10b981;#10b981;#9ca3af'
              keyTimes='0;0.79;0.81;0.83;0.85;0.87;0.95;0.99;1'
              dur='20s'
              repeatCount='indefinite'
            />
          </path>
          <text x='55' y='478' textAnchor='middle' fill='#64748b' fontSize='10' fontWeight='600'>
            <animate
              attributeName='fill'
              values='#64748b;#64748b;#64748b;#64748b;#64748b;#10b981;#10b981;#10b981;#64748b'
              keyTimes='0;0.79;0.81;0.83;0.85;0.87;0.95;0.99;1'
              dur='20s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='font-weight'
              values='600;600;600;600;600;700;700;700;600'
              keyTimes='0;0.79;0.81;0.83;0.85;0.87;0.95;0.99;1'
              dur='20s'
              repeatCount='indefinite'
            />
            Export
          </text>
        </g>

        {/* Subtle ambient glow - way in the back */}
        <circle cx='430' cy='311' r='200' fill='url(#glowGradient)' opacity='0.15'>
          <animate attributeName='r' values='200;230;200' dur='8s' repeatCount='indefinite' />
        </circle>

        {/* ===== SEQUENCE 1: FRAMED QUILT (0-4s) ===== */}
        <g opacity='0'>
          <animate
            attributeName='opacity'
            values='0;1;1;1;0;0'
            keyTimes='0;0.025;0.15;0.19;0.21;1'
            dur='20s'
            begin='0s'
            fill='freeze'
            repeatCount='indefinite'
          />

          {/* Large Rotating Frame - Centered */}
          <g
            transform='translate(230, 105)'
            // filter='url(#shadow)'
          >
            {/* Outer frame - SLOW COLOR TRANSITIONS (FOCUS) */}
            <rect x='0' y='0' width='390' height='390' rx='24' fill='#1e3a8a'>
              <animate
                attributeName='fill'
                values='#1e3a8a;#7c3aed;#1e3a8a'
                dur='12s'
                repeatCount='indefinite'
              />
            </rect>

            {/* Middle frame layer - SLOW COLOR TRANSITIONS (FOCUS) */}
            <rect x='15' y='15' width='360' height='360' rx='20' fill='#3b82f6'>
              <animate
                attributeName='fill'
                values='#3b82f6;#a78bfa;#3b82f6'
                dur='12s'
                repeatCount='indefinite'
              />
            </rect>

            {/* Inner canvas background - like editor */}
            <rect x='30' y='30' width='330' height='330' rx='16' fill='#1a1f2e' opacity='0.95' />

            {/* Content area - Simple 2x2 icon grid (NOT the focus) */}
            <g transform='translate(10, 10)'>
              {/* Top-left: Star */}
              <g>
                <path
                  d='M 110 60 L 123 100 L 165 100 L 132 125 L 145 165 L 110 140 L 75 165 L 88 125 L 55 100 L 97 100 Z'
                  fill='#fbbf24'
                  opacity='0.8'
                />
              </g>

              {/* Top-right: Pink cheetah pattern */}
              <g>
                <rect
                  x='200'
                  y='50'
                  width='120'
                  height='120'
                  rx='12'
                  fill='#fce7f3'
                  opacity='0.8'
                />
                <circle cx='220' cy='70' r='8' fill='#ec4899' opacity='0.7' />
                <circle cx='250' cy='75' r='6' fill='#ec4899' opacity='0.7' />
                <circle cx='280' cy='70' r='7' fill='#ec4899' opacity='0.7' />
                <circle cx='300' cy='80' r='5' fill='#ec4899' opacity='0.7' />
                <circle cx='235' cy='100' r='9' fill='#ec4899' opacity='0.7' />
                <circle cx='270' cy='105' r='6' fill='#ec4899' opacity='0.7' />
                <circle cx='215' cy='120' r='7' fill='#ec4899' opacity='0.7' />
                <circle cx='255' cy='130' r='8' fill='#ec4899' opacity='0.7' />
                <circle cx='295' cy='125' r='6' fill='#ec4899' opacity='0.7' />
                <circle cx='230' cy='145' r='5' fill='#ec4899' opacity='0.7' />
                <circle cx='275' cy='150' r='7' fill='#ec4899' opacity='0.7' />
              </g>

              {/* Bottom-left: Pink slanted stripes (diagonal to top-right) */}
              <g>
                <rect
                  x='50'
                  y='200'
                  width='120'
                  height='120'
                  rx='12'
                  fill='#2a2f3e'
                  opacity='0.8'
                />
                <line
                  x1='40'
                  y1='320'
                  x2='90'
                  y2='200'
                  stroke='#ec4899'
                  strokeWidth='12'
                  opacity='0.8'
                />
                <line
                  x1='65'
                  y1='320'
                  x2='115'
                  y2='200'
                  stroke='#ec4899'
                  strokeWidth='12'
                  opacity='0.8'
                />
                <line
                  x1='90'
                  y1='320'
                  x2='140'
                  y2='200'
                  stroke='#ec4899'
                  strokeWidth='12'
                  opacity='0.8'
                />
                <line
                  x1='115'
                  y1='320'
                  x2='165'
                  y2='200'
                  stroke='#ec4899'
                  strokeWidth='12'
                  opacity='0.8'
                />
                <line
                  x1='140'
                  y1='320'
                  x2='190'
                  y2='200'
                  stroke='#ec4899'
                  strokeWidth='12'
                  opacity='0.8'
                />
              </g>

              {/* Bottom-right: Pattern icon */}
              <rect
                x='200'
                y='200'
                width='120'
                height='120'
                rx='12'
                fill='url(#dots)'
                opacity='0.7'
              />
            </g>

            {/* Frame corner accents - MATCH FRAME COLORS */}
            <circle cx='30' cy='30' r='8' fill='white' opacity='0.9'>
              <animate
                attributeName='opacity'
                values='0.9;1;0.9'
                dur='2s'
                repeatCount='indefinite'
              />
            </circle>
            <circle cx='360' cy='30' r='8' fill='white' opacity='0.9'>
              <animate
                attributeName='opacity'
                values='0.9;1;0.9'
                dur='2s'
                begin='0.5s'
                repeatCount='indefinite'
              />
            </circle>
            <circle cx='30' cy='360' r='8' fill='white' opacity='0.9'>
              <animate
                attributeName='opacity'
                values='0.9;1;0.9'
                dur='2s'
                begin='1s'
                repeatCount='indefinite'
              />
            </circle>
            <circle cx='360' cy='360' r='8' fill='white' opacity='0.9'>
              <animate
                attributeName='opacity'
                values='0.9;1;0.9'
                dur='2s'
                begin='1.5s'
                repeatCount='indefinite'
              />
            </circle>
          </g>
        </g>

        {/* ===== SEQUENCE 2: PATTERN GRID BUILDING (4-8s) ===== */}
        <g opacity='0'>
          <animate
            attributeName='opacity'
            values='0;0;1;1;1;0;0'
            keyTimes='0;0.19;0.21;0.35;0.39;0.41;1'
            dur='20s'
            begin='0s'
            fill='freeze'
            repeatCount='indefinite'
          />

          {/* 3x3 Grid Building Animation - Centered in canvas */}
          <g transform='translate(230, 105)'>
            {/* Row 1 */}
            <rect x='0' y='0' width='120' height='120' rx='12' fill='url(#geometric)' />
            <rect x='130' y='0' width='120' height='120' rx='12' fill='#fbbf24' />
            <rect x='260' y='0' width='120' height='120' rx='12' fill='url(#pattern2)' />

            {/* Row 2 */}
            <rect x='0' y='130' width='120' height='115' rx='12' fill='url(#stripes)' />
            <rect x='130' y='130' width='120' height='115' rx='12' fill='#60a5fa' />
            <rect x='260' y='130' width='120' height='115' rx='12' fill='url(#pattern3)' />

            {/* Row 3 */}
            <rect x='0' y='255' width='120' height='115' rx='12' fill='url(#chevron)' />
            <rect x='130' y='255' width='120' height='115' rx='12' fill='#8b5cf6' />
            <rect x='260' y='255' width='120' height='115' rx='12' fill='url(#stars)' />
          </g>
        </g>

        {/* ===== SEQUENCE 3: COLOR CUSTOMIZATION (8-12s) ===== */}
        <g opacity='0'>
          <animate
            attributeName='opacity'
            values='0;0;1;1;1;0;0'
            keyTimes='0;0.39;0.41;0.55;0.59;0.61;1'
            dur='20s'
            begin='0s'
            fill='freeze'
            repeatCount='indefinite'
          />

          {/* Split view: Color Palette + Live Preview */}
          <g transform='translate(140, 110)'>
            {/* Color Palette Grid - Left side */}
            <g transform='translate(0, 0)'>
              <rect
                x='0'
                y='0'
                width='85'
                height='85'
                rx='12'
                fill='#6366f1'
                // filter='url(#softGlow)'
              >
                <animate
                  attributeName='fill'
                  values='#6366f1;#8b5cf6;#6366f1'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='0' y='95' width='85' height='85' rx='12' fill='#ec4899'>
                <animate
                  attributeName='fill'
                  values='#ec4899;#f43f5e;#ec4899'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='0' y='190' width='85' height='85' rx='12' fill='#10b981'>
                <animate
                  attributeName='fill'
                  values='#10b981;#059669;#10b981'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='0' y='285' width='85' height='85' rx='12' fill='#f59e0b'>
                <animate
                  attributeName='fill'
                  values='#f59e0b;#ef4444;#f59e0b'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
            </g>

            {/* Arrow indicator */}
            <g transform='translate(100, 180)'>
              <path
                d='M 0 0 L 20 10 L 0 20'
                stroke='#6366f1'
                strokeWidth='3'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <animate
                  attributeName='opacity'
                  values='0.5;1;0.5'
                  dur='1.5s'
                  repeatCount='indefinite'
                />
              </path>
            </g>

            {/* Live Preview - Right side with updating colors */}
            <g transform='translate(140, 0)'>
              <rect
                x='0'
                y='0'
                width='440'
                height='370'
                rx='16'
                fill='white'
                // filter='url(#shadow)'
              />
              <rect x='15' y='15' width='410' height='340' rx='12' fill='#f8fafc' />

              {/* Preview pattern with animated colors */}
              <rect x='30' y='30' width='190' height='160' rx='10' fill='#6366f1'>
                <animate
                  attributeName='fill'
                  values='#6366f1;#8b5cf6;#6366f1'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='230' y='30' width='180' height='160' rx='10' fill='#ec4899'>
                <animate
                  attributeName='fill'
                  values='#ec4899;#f43f5e;#ec4899'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='30' y='200' width='380' height='140' rx='10' fill='#10b981'>
                <animate
                  attributeName='fill'
                  values='#10b981;#059669;#10b981'
                  dur='4s'
                  repeatCount='indefinite'
                />
              </rect>
            </g>
          </g>
        </g>

        {/* ===== SEQUENCE 4: VERTEX/MESH EDITING (12-16s) ===== */}
        <g opacity='0'>
          <animate
            attributeName='opacity'
            values='0;0;1;1;1;0;0'
            keyTimes='0;0.59;0.61;0.75;0.79;0.81;1'
            dur='20s'
            begin='0s'
            fill='freeze'
            repeatCount='indefinite'
          />

          {/* Large Star with Vertex Editing - Centered in canvas */}
          <g
            transform='translate(225, 90) scale(1.35)'
            // filter='url(#shadow)'
          >
            {/* Center circle */}
            <circle cx='155' cy='170' r='60' fill='#f3f4f6' stroke='#d1d5db' strokeWidth='3' />

            {/* Star outline */}
            <path
              d='M 155 30 L 182 125 L 280 125 L 205 180 L 232 275 L 155 220 L 78 275 L 105 180 L 30 125 L 128 125 Z'
              fill='none'
              stroke='#9ca3af'
              strokeWidth='3.5'
            />

            {/* Star sections filling with patterns */}
            <path d='M 155 30 L 182 125 L 155 170 Z' fill='url(#pattern1)' />
            <path d='M 182 125 L 280 125 L 155 170 Z' fill='url(#dots)' />
            <path d='M 280 125 L 205 180 L 155 170 Z' fill='url(#chevron)' />
            <path d='M 205 180 L 232 275 L 155 170 Z' fill='url(#geometric)' />
            <path d='M 232 275 L 155 220 L 155 170 Z' fill='url(#stars)' />
            <path d='M 155 220 L 78 275 L 155 170 Z' fill='url(#pattern2)' />
            <path d='M 78 275 L 105 180 L 155 170 Z' fill='url(#stripes)' />
            <path d='M 105 180 L 30 125 L 155 170 Z' fill='url(#pattern3)' />
            <path d='M 30 125 L 128 125 L 155 170 Z' fill='url(#pattern4)' />
            <path d='M 128 125 L 155 30 L 155 170 Z' fill='#fbbf24' />

            {/* Vertex points with glow */}
            <circle
              cx='155'
              cy='30'
              r='7'
              fill='#6366f1'
              stroke='white'
              strokeWidth='3'
              // filter='url(#glow)'
            >
              <animate attributeName='r' values='7;9;7' dur='1.2s' repeatCount='indefinite' />
            </circle>
            <circle
              cx='280'
              cy='125'
              r='7'
              fill='#6366f1'
              stroke='white'
              strokeWidth='3'
              // filter='url(#glow)'
            >
              <animate
                attributeName='r'
                values='7;9;7'
                dur='1.2s'
                begin='0.2s'
                repeatCount='indefinite'
              />
            </circle>
            <circle
              cx='232'
              cy='275'
              r='7'
              fill='#6366f1'
              stroke='white'
              strokeWidth='3'
              // filter='url(#glow)'
            >
              <animate
                attributeName='r'
                values='7;9;7'
                dur='1.2s'
                begin='0.4s'
                repeatCount='indefinite'
              />
            </circle>
            <circle
              cx='155'
              cy='220'
              r='7'
              fill='#6366f1'
              stroke='white'
              strokeWidth='3'
              // filter='url(#glow)'
            >
              <animate
                attributeName='r'
                values='7;9;7'
                dur='1.2s'
                begin='0.6s'
                repeatCount='indefinite'
              />
            </circle>
            <circle
              cx='78'
              cy='275'
              r='7'
              fill='#6366f1'
              stroke='white'
              strokeWidth='3'
              // filter='url(#glow)'
            >
              <animate
                attributeName='r'
                values='7;9;7'
                dur='1.2s'
                begin='0.8s'
                repeatCount='indefinite'
              />
            </circle>
            <circle
              cx='30'
              cy='125'
              r='7'
              fill='#6366f1'
              stroke='white'
              strokeWidth='3'
              // filter='url(#glow)'
            >
              <animate
                attributeName='r'
                values='7;9;7'
                dur='1.2s'
                begin='1s'
                repeatCount='indefinite'
              />
            </circle>
          </g>
        </g>

        {/* ===== SEQUENCE 5: EXPORT READY / FINAL POLISH (16-20s) ===== */}
        <g opacity='0'>
          <animate
            attributeName='opacity'
            values='0;0;1;1;1;0'
            keyTimes='0;0.79;0.81;0.95;0.99;1'
            dur='20s'
            begin='0s'
            fill='freeze'
            repeatCount='indefinite'
          />

          {/* Final polished framed design */}
          <g transform='translate(230, 105)'>
            {/* Large finished frame */}
            <rect
              x='0'
              y='0'
              width='400'
              height='400'
              rx='20'
              fill='#1e3a8a'
              // filter='url(#shadow)'
            />
            <rect x='15' y='15' width='370' height='370' rx='16' fill='#3b82f6' />
            <rect x='30' y='30' width='340' height='340' rx='14' fill='white' />

            {/* Beautiful finished 2x2 pattern */}
            <rect x='48' y='48' width='155' height='155' rx='12' fill='url(#pattern1)' />
            <rect x='213' y='48' width='145' height='155' rx='12' fill='url(#dots)' />
            <rect x='48' y='213' width='155' height='145' rx='12' fill='url(#geometric)' />
            <rect x='213' y='213' width='145' height='145' rx='12' fill='url(#pattern2)' />

            {/* Inner frame highlight */}
            <rect
              x='44'
              y='44'
              width='300'
              height='300'
              rx='12'
              fill='none'
              stroke='#f3f4f6'
              strokeWidth='4'
            />
          </g>
        </g>

        {/* Canvas Area outline - AFTER all animations so it doesn't block them */}
        <rect
          x='125'
          y='75'
          width='610'
          height='473'
          rx='16'
          fill='none'
          stroke='rgba(255, 255, 255, 0.05)'
          strokeWidth='1'
        />
      </svg>
    </Box>
  )
}

export default AnimatedEditorShowcase
